import axios, { AxiosRequestConfig } from 'axios';
import type { ApercuReunion, BilanReunion, BilanScission, EcartDto, PassageAScinder, PorteeRapprochement, ResultatRapprochements, ResultatVoisins } from '../types/api';

const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json',
    },
});

const BASE = '/api/rapprochements';

/** La portée telle que l'URL la dit : `tous` | `vocabulaire:<id>` | `dossier:<id>`. */
export function porteeEnTexte(portee: PorteeRapprochement): string {
    return portee.sorte === 'tous' ? 'tous' : `${portee.sorte}:${portee.id}`;
}

/** Le code d'un refus de portée trop grande (409). */
export const PORTEE_TROP_GRANDE = 'PORTEE_TROP_GRANDE';

/** Les codes de refus de la réunion (C31 SP2) : 409 conflit d'une note, 409 trop de notes, 400 concept versé, 400 même concept. */
export const REUNION_CONFLIT = 'REUNION_CONFLIT';
export const REUNION_TROP_GRANDE = 'REUNION_TROP_GRANDE';
export const CONCEPT_VERSE = 'CONCEPT_VERSE';
export const MEME_CONCEPT = 'MEME_CONCEPT';

/** Les codes de refus de la scission (C31 bis SP3) : 400 nom déjà pris, 409 conflit d'une note, 409 trop de passages ou de notes. */
export const CONCEPT_EXISTANT = 'CONCEPT_EXISTANT';
export const SCISSION_CONFLIT = 'SCISSION_CONFLIT';
export const SCISSION_TROP_GRANDE = 'SCISSION_TROP_GRANDE';

const reunirUrl = (conserveId: string) => `/api/entrees/${encodeURIComponent(conserveId)}/reunir`;
const voisinsUrl = (entreeId: string) => `/api/entrees/${encodeURIComponent(entreeId)}/voisins`;
const scinderUrl = (entreeId: string) => `/api/entrees/${encodeURIComponent(entreeId)}/scinder`;

/**
 * Client de `rapprochements` (chantier C31 SP1) : lire les paires de concepts à
 * rapprocher dans une portée, écarter une paire et la reprendre, calculer les
 * empreintes manquantes sur geste. La lecture n'écrit rien ; les erreurs remontent
 * à l'appelant (le slice reprend le message et le code du serveur).
 */
export const rapprochementService = {
    lister: async (portee: PorteeRapprochement, accessToken: string): Promise<ResultatRapprochements> => {
        const response = await axios.get(`${BASE}?portee=${encodeURIComponent(porteeEnTexte(portee))}`, createConfig(accessToken));
        return response.data.data;
    },
    ecarts: async (accessToken: string): Promise<EcartDto[]> => {
        const response = await axios.get(`${BASE}/ecarts`, createConfig(accessToken));
        return response.data.data;
    },
    ecarter: async (ecart: { a: string; b: string; raison: 'ecarte' | 'distingue' }, accessToken: string): Promise<EcartDto> => {
        const response = await axios.post(`${BASE}/ecarts`, ecart, createConfig(accessToken));
        return response.data.data;
    },
    reprendre: async (a: string, b: string, accessToken: string): Promise<void> => {
        await axios.delete(`${BASE}/ecarts?a=${encodeURIComponent(a)}&b=${encodeURIComponent(b)}`, createConfig(accessToken));
    },
    calculerEmpreintes: async (portee: PorteeRapprochement, accessToken: string): Promise<{ calculees: number; restantes: number }> => {
        const response = await axios.post(`${BASE}/empreintes`, { portee: porteeEnTexte(portee) }, createConfig(accessToken));
        return response.data.data;
    },
    /** C31 SP2 : l'aperçu en chiffres d'une réunion (n'écrit rien) ; `dossierId` = par dossier (C31 bis SP2). */
    apercuReunion: async (conserveId: string, reuniId: string, accessToken: string, dossierId?: string): Promise<ApercuReunion> => {
        const dossier = dossierId ? `&dossierId=${encodeURIComponent(dossierId)}` : '';
        const response = await axios.get(`${reunirUrl(conserveId)}?reuniId=${encodeURIComponent(reuniId)}${dossier}`, createConfig(accessToken));
        return response.data.data;
    },
    /** C31 SP3 : les concepts voisins d'un concept de la personne, sur sa fiche (n'écrit rien). */
    voisins: async (entreeId: string, accessToken: string): Promise<ResultatVoisins> => {
        const response = await axios.get(voisinsUrl(entreeId), createConfig(accessToken));
        return response.data.data;
    },
    /** C31 SP2 : réunit `reuniId` dans `conserveId` ; idempotent, « Réessayer » renvoie le même appel ; `dossierId` = par dossier (C31 bis SP2). */
    reunir: async (conserveId: string, corps: { reuniId: string; definition?: string; dossierId?: string }, accessToken: string): Promise<BilanReunion> => {
        const response = await axios.post(reunirUrl(conserveId), corps, createConfig(accessToken));
        return response.data.data;
    },
    /** C31 bis SP3 : fait naître de `entreeId` un concept plus précis et y déplace les passages cochés ; idempotent, « Réessayer » renvoie le même appel. */
    scinder: async (entreeId: string, corps: { nom: string; definition?: string; passages: PassageAScinder[] }, accessToken: string): Promise<BilanScission> => {
        const response = await axios.post(scinderUrl(entreeId), corps, createConfig(accessToken));
        return response.data.data;
    },
};

export default rapprochementService;
